import { useCallback, useMemo } from 'react'

import type { AITranslationGlossaryEntryResponseDto } from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button'
import { EmptyProvider } from '@/components/ui/empty/empty-provider'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import DeleteIcon from '@/modules/chatting/icons/delete.svg'

import {
  $glossaryEntries,
  addGlossaryEntryFx,
  removeGlossaryEntryFx,
} from '../../models/glossary-settings'

export const SmartGlossary = () => {
  const entries = useUnit($glossaryEntries)
  const [isAddingEntry, isRemovingEntry] = useUnit([
    addGlossaryEntryFx.pending,
    removeGlossaryEntryFx.pending,
  ])

  const handleRemoveWord = useCallback((id: number) => {
    openDialog({
      type: DIALOG_TYPE.CONFIRMATION,
      title: 'Are you sure you want to delete the word?',
      onConfirm: () => removeGlossaryEntryFx(id),
    })
  }, [])

  const columns = useMemo<ColumnDef<AITranslationGlossaryEntryResponseDto>[]>(
    () => [
      {
        accessorKey: 'original',
        header: 'Word',
        cell: ({ row }) => (
          <div className="text-sm">{row.original.original}</div>
        ),
      },
      {
        accessorKey: 'translation',
        header: 'Translation',
        cell: ({ row }) => (
          <div className="text-sm">{row.original.translation}</div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <button
              onClick={() => handleRemoveWord(row.original.id)}
              className="text-danger-default hover:text-danger-hover"
              disabled={isRemovingEntry}
            >
              <DeleteIcon />
            </button>
          </div>
        ),
      },
    ],
    [handleRemoveWord, isRemovingEntry],
  )

  const openAddWordDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.SMART_GLOSSARY_AI_DIALOG,
    })
  }, [])
  const renderEmpty = useCallback(
    () => (
      <EmptyTableContent>
        It looks like you don&#39;t have any words created yet, click on the{' '}
        <span className="text-pointer text-primary-500">Add Word</span> button
        to do so.
      </EmptyTableContent>
    ),
    [],
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-medium">Smart Glossary</h2>
          <p className="text-sm text-secondary-300">
            Add words that should be translated or kept unchanged in your own
            outgoing messages. This does not affect messages received from
            subscribers.
          </p>
        </div>
        <Button
          onClick={openAddWordDialog}
          disabled={isAddingEntry}
          isLoading={isAddingEntry}
        >
          <PlusIcon /> Add Word
        </Button>
      </div>

      <div className="flex flex-col gap-4">
        <EmptyProvider render={renderEmpty}>
          <DataTable
            columns={columns}
            data={entries}
            hasActionColumn
            tbodyMinHeight={false}
          />
        </EmptyProvider>
      </div>
    </div>
  )
}
